import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  CheckCircle2,
  Loader2,
  Play,
  PlugZap,
  RefreshCw,
  Search,
  Save,
  TerminalSquare,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/toaster';
import {
  useClickupAvailableSpaces,
  useClickupSyncLogs,
  useClickupSyncSettings,
  useClearClickupSyncLogs,
  useRunClickupSyncNow,
  useTestClickupConnection,
  useUpdateClickupSyncSettings,
} from '@/hooks/use-clickup-sync-admin';
import type { ClickupAvailableSpace, ClickupSyncRunStatus } from '@/types';
import { formatDisplayDateTime } from '@/lib/date-format';

type Totals = Record<string, { created?: number; updated?: number; skipped?: number; failed?: number }>;

function statusBadgeVariant(status: ClickupSyncRunStatus): 'default' | 'secondary' | 'destructive' {
  if (status === 'success') return 'default';
  if (status === 'failed') return 'destructive';
  return 'secondary';
}

/** Production builds hide/disable Run Now unless explicitly opted in (backend still enforces). */
const isClickupRunNowUiEnabled =
  !import.meta.env.PROD || import.meta.env.VITE_ALLOW_CLICKUP_RUN_NOW === 'true';

export function ClickupSyncCard() {
  const { data: settings, isLoading } = useClickupSyncSettings();
  const { data: logsData } = useClickupSyncLogs(200);
  const updateSettings = useUpdateClickupSyncSettings();
  const testConnection = useTestClickupConnection();
  const runSync = useRunClickupSyncNow();
  const clearLogs = useClearClickupSyncLogs();
  const [spacePickerOpen, setSpacePickerOpen] = useState(false);
  const [spaceQuery, setSpaceQuery] = useState('');
  const {
    data: availableSpacesData,
    isLoading: availableSpacesLoading,
    isFetching: availableSpacesFetching,
    isError: availableSpacesError,
    refetch: refetchAvailableSpaces,
  } = useClickupAvailableSpaces(Boolean(settings?.hasToken));

  const [tokenInput, setTokenInput] = useState('');
  const [spaces, setSpaces] = useState<string[]>([]);
  const [recentOnly, setRecentOnly] = useState(false);
  const [requestsPerSecond, setRequestsPerSecond] = useState(1);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const settingsUpdatedAt = settings?.updatedAt;
  useEffect(() => {
    if (!settings) return;
    setSpaces(settings.spaces.map((s) => s.spaceId));
    setRecentOnly(settings.recentOnly ?? false);
    setRequestsPerSecond(settings.requestsPerSecond ?? 1);
  }, [settingsUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    const raw = settings?.lastRun?.summaryJson as { totals?: Totals } | null | undefined;
    return raw?.totals ?? {};
  }, [settings?.lastRun?.summaryJson]);

  const removeSpace = (spaceId: string) => {
    setSpaces((prev) => prev.filter((item) => item !== spaceId));
  };

  const addSpaceFromOption = (space: ClickupAvailableSpace) => {
    if (spaces.includes(space.spaceId)) {
      return;
    }
    setSpaces((prev) => [...prev, space.spaceId]);
    setSpaceQuery('');
  };

  const availableSpaces = useMemo(
    () => availableSpacesData?.spaces ?? [],
    [availableSpacesData?.spaces],
  );
  const availableSpaceMap = useMemo(
    () => new Map(availableSpaces.map((space) => [space.spaceId, space])),
    [availableSpaces],
  );
  const filteredSpaces = useMemo(() => {
    if (!spaceQuery.trim()) return availableSpaces;
    const query = spaceQuery.trim().toLowerCase();
    return availableSpaces.filter((space) => {
      return (
        space.spaceName.toLowerCase().includes(query) ||
        space.workspaceName.toLowerCase().includes(query) ||
        space.spaceId.toLowerCase().includes(query) ||
        space.workspaceId.toLowerCase().includes(query)
      );
    });
  }, [availableSpaces, spaceQuery]);

  useEffect(() => {
    if (!availableSpacesError) return;
    toast({
      title: 'Failed to fetch available ClickUp spaces',
      variant: 'destructive',
    });
  }, [availableSpacesError]);

  useEffect(() => {
    if (!availableSpacesData?.warnings?.length) return;
    toast({
      title: 'Some workspaces could not be loaded',
      description: availableSpacesData.warnings[0],
      variant: 'destructive',
    });
  }, [availableSpacesData?.warnings]);

  const onSaveSettings = async () => {
    try {
      await updateSettings.mutateAsync({
        token: tokenInput.trim() ? tokenInput.trim() : undefined,
        spaces,
        recentOnly,
        requestsPerSecond,
      });
      setTokenInput('');
      toast({ title: 'ClickUp sync settings saved' });
    } catch {
      toast({ title: 'Failed to save ClickUp sync settings', variant: 'destructive' });
    }
  };

  const onTestConnection = async () => {
    try {
      const result = await testConnection.mutateAsync();
      toast({
        title: result.success ? 'Connection successful' : 'Connection failed',
        description: result.checkedSpaces.length
          ? `Validated ${result.checkedSpaces.length} configured space(s).`
          : result.message,
        variant: result.success ? 'default' : 'destructive',
      });
    } catch {
      toast({ title: 'ClickUp connection test failed', variant: 'destructive' });
    }
  };

  const onRunSyncNow = async () => {
    try {
      const result = await runSync.mutateAsync();
      toast({
        title: 'Sync run finished',
        description: `Status: ${result.run.status}`,
      });
    } catch {
      toast({ title: 'Sync run failed to start or complete', variant: 'destructive' });
    }
  };

  const onClearLogs = async () => {
    try {
      await clearLogs.mutateAsync();
      setConfirmClearOpen(false);
      toast({ title: 'ClickUp sync logs cleared' });
    } catch {
      toast({ title: 'Failed to clear logs', variant: 'destructive' });
    }
  };

  const isMutating =
    updateSettings.isPending ||
    testConnection.isPending ||
    (isClickupRunNowUiEnabled && runSync.isPending) ||
    clearLogs.isPending;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <TerminalSquare className="h-5 w-5" />
          <CardTitle>ClickUp Sync</CardTitle>
        </div>
        <CardDescription>
          Configure token/spaces, run sync manually, and inspect run logs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="clickup-token">ClickUp Token (write-only)</Label>
          <Input
            id="clickup-token"
            type="password"
            value={tokenInput}
            onChange={(event) => setTokenInput(event.target.value)}
            placeholder="Paste new token to update"
            disabled={isMutating}
          />
          <p className="text-xs text-muted-foreground">
            Stored token: {settings?.hasToken ? `****${settings.tokenLast4}` : 'Not configured'}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label>ClickUp Space IDs</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Where to find your ClickUp Space ID"
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border/70 text-xs text-muted-foreground/80 transition-opacity hover:text-foreground hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 opacity-80"
                  >
                    ?
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs leading-relaxed">
                  <div className="space-y-1">
                    <p className="font-medium text-foreground">Where to find your Space ID</p>
                    <p>Find it in your ClickUp URL.</p>
                    <p className="break-all text-muted-foreground">
                      https://app.clickup.com/25702504/v/l/li/...
                    </p>
                    <p>
                      {'->'} Space ID = <span className="font-medium">25702504</span>
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex gap-2">
            <Popover open={spacePickerOpen} onOpenChange={setSpacePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={spacePickerOpen}
                  className="flex-1 justify-between"
                  disabled={isMutating || !settings?.hasToken}
                >
                  <span className="truncate">
                    {settings?.hasToken ? 'Select ClickUp spaces' : 'Configure token to load spaces'}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[460px] p-0">
                <div className="flex items-center border-b px-3">
                  <Search className="h-4 w-4 shrink-0 opacity-50" />
                  <Input
                    placeholder="Search by space/workspace name or ID..."
                    value={spaceQuery}
                    onChange={(event) => setSpaceQuery(event.target.value)}
                    className="h-10 border-0 focus-visible:ring-0"
                  />
                </div>
                <ScrollArea className="h-64">
                  {availableSpacesLoading || availableSpacesFetching ? (
                    <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading spaces...
                    </div>
                  ) : filteredSpaces.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No spaces found for this token.
                    </div>
                  ) : (
                    <div className="p-1">
                      {filteredSpaces.map((space) => {
                        const selected = spaces.includes(space.spaceId);
                        return (
                          <button
                            key={`${space.workspaceId}:${space.spaceId}`}
                            type="button"
                            disabled={selected}
                            onClick={() => addSpaceFromOption(space)}
                            className="w-full rounded-sm px-2 py-2 text-left hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <p className="text-sm font-medium">{space.spaceName}</p>
                            <p className="text-xs text-muted-foreground">
                              {space.workspaceName} · space {space.spaceId}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
            <Button
              type="button"
              variant="outline"
              onClick={() => refetchAvailableSpaces()}
              disabled={isMutating || !settings?.hasToken || availableSpacesFetching}
            >
              {availableSpacesFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-2">Refresh</span>
            </Button>
          </div>
          {!settings?.hasToken && (
            <p className="text-xs text-muted-foreground">
              Save a ClickUp token first to discover available spaces.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {spaces.length === 0 ? (
              <span className="text-xs text-muted-foreground">No spaces configured</span>
            ) : (
              spaces.map((spaceId) => (
                <Badge key={spaceId} variant="secondary" className="gap-2">
                  <span className="max-w-[280px] truncate">
                    {availableSpaceMap.get(spaceId)?.spaceName ?? spaceId}
                  </span>
                  <span className="text-[10px] text-muted-foreground">({spaceId})</span>
                  <button
                    type="button"
                    onClick={() => removeSpace(spaceId)}
                    className="text-muted-foreground hover:text-foreground"
                    disabled={isMutating}
                    aria-label={`Remove ${spaceId}`}
                  >
                    ×
                  </button>
                </Badge>
              ))
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="recent-only"
            checked={recentOnly}
            onChange={(e) => setRecentOnly(e.target.checked)}
            disabled={isMutating}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <Label htmlFor="recent-only" className="text-sm font-normal cursor-pointer">
            Update last 3 months only
          </Label>
        </div>

        <div className="flex items-center space-x-3">
          <Label htmlFor="rate-limit" className="text-sm font-normal whitespace-nowrap">
            API Rate Limit:
          </Label>
          <select
            id="rate-limit"
            value={requestsPerSecond}
            onChange={(e) => setRequestsPerSecond(Number(e.target.value))}
            disabled={isMutating}
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <option value={1}>1 req/sec (safe)</option>
            <option value={2}>2 req/sec</option>
            <option value={3}>3 req/sec</option>
            <option value={4}>4 req/sec (fast)</option>
          </select>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Button onClick={onSaveSettings} disabled={isMutating}>
            {updateSettings.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save settings
          </Button>
          <Button variant="outline" onClick={onTestConnection} disabled={isMutating}>
            {testConnection.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <PlugZap className="mr-2 h-4 w-4" />
            )}
            Test connection
          </Button>
          <Button
            variant="default"
            onClick={onRunSyncNow}
            disabled={isMutating || !isClickupRunNowUiEnabled}
          >
            {runSync.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Run sync now
          </Button>
        </div>
        {!isClickupRunNowUiEnabled && (
          <p className="text-xs text-muted-foreground">
            Run sync now is disabled in production builds. Set{' '}
            <span className="font-mono">VITE_ALLOW_CLICKUP_RUN_NOW=true</span> at build time only if
            operators intentionally need the control; the API remains separately guarded.
          </p>
        )}

        <div className="space-y-2 rounded-md border p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Last run</h4>
            {settings?.lastRun ? (
              <Badge variant={statusBadgeVariant(settings.lastRun.status)}>{settings.lastRun.status}</Badge>
            ) : (
              <Badge variant="secondary">none</Badge>
            )}
          </div>
          {settings?.lastRun ? (
            <div className="space-y-1 text-xs text-muted-foreground">
              <p>Started: {formatDisplayDateTime(settings.lastRun.startedAt)}</p>
              <p>
                Finished:{' '}
                {settings.lastRun.finishedAt
                  ? formatDisplayDateTime(settings.lastRun.finishedAt)
                  : 'in progress'}
              </p>
              <p>Spaces: {settings.lastRun.spacesJson.join(', ') || 'none'}</p>
              {Object.keys(totals).length > 0 && (
                <div className="grid gap-1 pt-2">
                  {Object.entries(totals).map(([entity, counts]) => (
                    <p key={entity}>
                      {entity}: c={counts.created ?? 0} u={counts.updated ?? 0} s={counts.skipped ?? 0} f=
                      {counts.failed ?? 0}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No run executed yet.</p>
          )}
        </div>

        <div className="space-y-2 rounded-md border p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Run logs</h4>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmClearOpen(true)}
              disabled={isMutating}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Clear logs
            </Button>
          </div>
          <ScrollArea className="h-64 rounded border">
            <div className="space-y-2 p-3">
              {logsData?.logs?.length ? (
                logsData.logs.map((log) => (
                  <div key={log.id} className="rounded border bg-background p-2 text-xs">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-muted-foreground">
                        {formatDisplayDateTime(log.timestamp)}
                      </span>
                      <Badge
                        variant={log.level === 'error' ? 'destructive' : 'secondary'}
                        className="uppercase"
                      >
                        {log.level}
                      </Badge>
                    </div>
                    <p className="break-words">{log.message}</p>
                  </div>
                ))
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-xs">No logs yet.</span>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
          <div className="mb-1 flex items-center gap-2 font-medium text-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            Security
          </div>
          Token is encrypted at rest and never returned to the UI. This field is write-only.
        </div>
      </CardContent>

      <AlertDialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear ClickUp sync logs?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes all sync logs from the ClickUp Sync panel. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onClearLogs} className="bg-destructive hover:bg-destructive/90">
              Clear logs
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

